import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CalendarClock, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

function prettyDate(dateStr) {
  try {
    const [y, m, d] = dateStr.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return `${WEEKDAYS[dt.getDay()]}, ${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
  } catch { return dateStr; }
}

const inputCls = "w-full rounded-xl bg-white border border-[#5B6E5B]/15 px-4 py-3 font-body text-sm text-[#2C332A] placeholder:text-[#A8B0A2] outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#C87A5E] focus:ring-2 focus:ring-[#C87A5E]/25";

export default function SlotDialog({ slot, onClose }) {
  const open = !!slot;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (slot) {
      setForm({ name: "", email: "", phone: "", message: `Olá! Tenho interesse no horário de ${prettyDate(slot.date)} às ${slot.time}. Gostaria de reservar este atendimento online.` });
    }
  }, [slot]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Preencha nome, e-mail e uma mensagem.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, slot_date: slot.date, slot_time: slot.time });
      toast.success("Mensagem enviada! Em breve entrarei em contato. 💚");
      onClose();
    } catch {
      toast.error("Não foi possível enviar. Tente novamente em instantes.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#FDFBF7]" data-testid="slot-contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2C332A]">Reservar horário</DialogTitle>
          <DialogDescription className="font-body text-sm text-[#7A8A7A]">
            Envie sua mensagem para reservar o atendimento neste horário.
          </DialogDescription>
        </DialogHeader>
        {slot && (
          <div className="flex items-center gap-3 rounded-xl bg-[#5B6E5B]/8 px-4 py-3 mb-1" data-testid="slot-dialog-info">
            <CalendarClock className="w-5 h-5 text-[#5B6E5B]" strokeWidth={1.5} />
            <div>
              <p className="font-body text-sm font-medium text-[#2C332A] capitalize">{prettyDate(slot.date)}</p>
              <p className="font-body text-xs text-[#7A8A7A]">às {slot.time} · atendimento online</p>
            </div>
          </div>
        )}
        <form onSubmit={submit} className="space-y-4" data-testid="slot-contact-form">
          <input data-testid="slot-input-name" value={form.name} onChange={update("name")} placeholder="Seu nome" className={inputCls} />
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="slot-input-email" type="email" value={form.email} onChange={update("email")} placeholder="voce@email.com" className={inputCls} />
            <input data-testid="slot-input-phone" value={form.phone} onChange={update("phone")} placeholder="(00) 00000-0000" className={inputCls} />
          </div>
          <textarea data-testid="slot-input-message" value={form.message} onChange={update("message")} rows={4} className={`${inputCls} resize-none`} />
          <button type="submit" disabled={loading} data-testid="slot-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#5B6E5B] px-8 py-3.5 text-[#FDFBF7] transition-[transform,background-color] duration-300 hover:bg-[#4A5D4E] hover:scale-[1.02] disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.6} />}
            <span className="font-body">{loading ? "Enviando..." : "Enviar mensagem"}</span>
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
